import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './WeddingPhotosPage.css';

interface PhotoData {
  [category: string]: string[];
}

interface UploadFile {
  file: File;
  preview: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
}

const API_ENDPOINT =
  process.env.REACT_APP_RSVP_API_ENDPOINT ||
  "https://michael-and-ashlynn.com/rsvp";

const categoryLabels: Record<string, string> = {
  bridal_party: 'Bridal Party',
  bride_and_groom: 'Bride & Groom',
  ceremony: 'Ceremony',
  dinner: 'Dinner',
  dancing: 'Dancing',
  friends_and_family: 'Friends & Family',
};

// Configure which photo index to use as the preview for each album
// Change the number to use a different photo (0 = first photo, 1 = second, etc.)
const categoryPreviewIndex: Record<string, number> = {
  bridal_party: 141,
  bride_and_groom: 350,
  ceremony: 174,
  dinner: 184,
  dancing: 60,
  friends_and_family: 108,
  engagement: 52,
};

const WeddingPhotosPage: React.FC = () => {
  const [photoData, setPhotoData] = useState<PhotoData | null>(null);
  const [engagementPhotos, setEngagementPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [uploaderName, setUploaderName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadSectionRef = useRef<HTMLElement>(null);

  const scrollToUpload = () => {
    uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    // Fetch wedding photos (required)
    fetch('/wedding_photo_urls.json')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load wedding photo data');
        return res.json();
      })
      .then((data) => {
        setPhotoData(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });

    // Fetch engagement photos separately (optional)
    fetch('/engagement_photo_urls.json')
      .then((res) => {
        if (!res.ok) return null;
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setEngagementPhotos(data);
        }
      })
      .catch(() => {
        // Silently ignore - engagement photos are optional
      });
  }, []);

  // File upload handlers
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const validFiles = Array.from(files).filter(file =>
      file.type.startsWith('image/')
    );

    if (validFiles.length === 0) {
      setUploadMessage({ type: 'error', text: 'Please select image files only.' });
      return;
    }

    if (uploadFiles.length + validFiles.length > 20) {
      setUploadMessage({ type: 'error', text: 'Maximum 20 photos per upload.' });
      return;
    }

    const newFiles: UploadFile[] = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      status: 'pending' as const,
      progress: 0
    }));

    setUploadFiles(prev => [...prev, ...newFiles]);
    setUploadMessage(null);
  }, [uploadFiles.length]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const removeFile = useCallback((index: number) => {
    setUploadFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      setUploadMessage({ type: 'error', text: 'Please select photos to upload.' });
      return;
    }

    if (!uploaderName.trim()) {
      setUploadMessage({ type: 'error', text: 'Please enter your name.' });
      return;
    }

    setIsUploading(true);
    setUploadMessage(null);

    try {
      // Get presigned URLs from the API
      const response = await fetch(`${API_ENDPOINT}/photo-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uploaderName: uploaderName.trim(),
          files: uploadFiles.map(f => ({
            fileName: f.file.name,
            contentType: f.file.type
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get upload URLs');
      }

      const { uploads } = await response.json();

      // Upload each file directly to S3
      let successCount = 0;
      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        const fileData = uploadFiles[i];

        setUploadFiles(prev => {
          const newFiles = [...prev];
          newFiles[i] = { ...newFiles[i], status: 'uploading', progress: 0 };
          return newFiles;
        });

        try {
          const uploadResponse = await fetch(upload.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': upload.contentType },
            body: fileData.file
          });

          if (uploadResponse.ok) {
            setUploadFiles(prev => {
              const newFiles = [...prev];
              newFiles[i] = { ...newFiles[i], status: 'success', progress: 100 };
              return newFiles;
            });
            successCount++;
          } else {
            throw new Error('Upload failed');
          }
        } catch {
          setUploadFiles(prev => {
            const newFiles = [...prev];
            newFiles[i] = { ...newFiles[i], status: 'error', progress: 0 };
            return newFiles;
          });
        }
      }

      if (successCount === uploads.length) {
        setUploadMessage({
          type: 'success',
          text: `Thank you! ${successCount} photo${successCount > 1 ? 's' : ''} uploaded successfully. We'll review and add them to the gallery soon!`
        });
        // Clear successful uploads after a moment
        setTimeout(() => {
          setUploadFiles(prev => prev.filter(f => f.status !== 'success'));
          setUploaderName('');
        }, 3000);
      } else {
        setUploadMessage({
          type: 'error',
          text: `${successCount} of ${uploads.length} photos uploaded. Some uploads failed.`
        });
      }
    } catch (err) {
      setUploadMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Upload failed. Please try again.'
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Get preview image for category using configured index
  const getPreviewImage = (category: string): string | null => {
    if (!photoData || !photoData[category] || photoData[category].length === 0) {
      return null;
    }
    const previewIndex = categoryPreviewIndex[category] ?? 0;
    // Ensure index is within bounds, fallback to first photo if out of range
    const safeIndex = Math.min(previewIndex, photoData[category].length - 1);
    return photoData[category][safeIndex];
  };

  const getCategoryLabel = (category: string): string => {
    return categoryLabels[category] || category.replace(/_/g, ' ');
  };

  if (loading) {
    return (
      <main className="page wedding-photos-page">
        <div className="container">
          <p className="loading-text">Loading photos...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="page wedding-photos-page">
        <div className="container">
          <p className="error-text">Error: {error}</p>
        </div>
      </main>
    );
  }

  // Use categoryLabels order, only include categories that exist in photoData
  const categories = Object.keys(categoryLabels).filter(
    (category) => photoData && photoData[category]
  );

  return (
    <main className="page wedding-photos-page">
      <section className="photos-landing">
        <div className="container">
          <h1 className="section-title">Wedding Photos</h1>
          <p className="photos-subtitle">Browse our wedding memories by category</p>

          {/* Call-to-action banner for photo uploads */}
          <div className="upload-cta-banner" onClick={scrollToUpload}>
            <div className="upload-cta-content">
              <svg className="upload-cta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="13" r="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="upload-cta-text">
                <span className="upload-cta-title">Have photos from the wedding?</span>
                <span className="upload-cta-subtitle">Share them with us! Click here to upload your favorites.</span>
              </div>
              <svg className="upload-cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          <div className="category-grid">
            {categories.map((category, idx) => {
              const previewUrl = getPreviewImage(category);
              const photoCount = photoData?.[category]?.length || 0;

              return (
                <Link
                  to={`/photos/${category}`}
                  key={category}
                  className="category-card"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="category-image-wrapper">
                    {previewUrl && (
                      <img
                        src={previewUrl}
                        alt={getCategoryLabel(category)}
                        className="category-preview"
                        loading="lazy"
                      />
                    )}
                    <div className="category-overlay">
                      <span className="view-text">View Photos</span>
                    </div>
                  </div>
                  <div className="category-info">
                    <h2 className="category-title">{getCategoryLabel(category)}</h2>
                    <span className="category-count">{photoCount} photos</span>
                  </div>
                </Link>
              );
            })}

            {/* Engagement Photos Card */}
            {engagementPhotos.length > 0 && (
              <Link
                to="/photos/engagement"
                className="category-card engagement-card"
                style={{ animationDelay: `${categories.length * 0.1}s` }}
              >
                <div className="category-image-wrapper">
                  <img
                    src={engagementPhotos[Math.min(categoryPreviewIndex.engagement ?? 0, engagementPhotos.length - 1)]}
                    alt="Engagement Photos"
                    className="category-preview"
                    loading="lazy"
                  />
                  <div className="category-overlay">
                    <span className="view-text">View Photos</span>
                  </div>
                </div>
                <div className="category-info">
                  <h2 className="category-title">Engagement</h2>
                  <span className="category-count">{engagementPhotos.length} photos</span>
                </div>
              </Link>
            )}
          </div>

          {/* Guest Photo Upload Section */}
          <section className="guest-upload-section" ref={uploadSectionRef}>
            <div className="upload-header">
              <h2 className="upload-title">Share Your Photos</h2>
              <p className="upload-subtitle">
                Have photos from the wedding? We'd love to see them! Upload your favorites and we'll add the best ones to the gallery.
              </p>
            </div>

            <div className="upload-form">
              <div className="uploader-name-field">
                <label htmlFor="uploaderName">Your Name</label>
                <input
                  type="text"
                  id="uploaderName"
                  value={uploaderName}
                  onChange={(e) => setUploaderName(e.target.value)}
                  placeholder="Enter your name"
                  disabled={isUploading}
                />
              </div>

              <div
                className={`upload-dropzone ${isDragOver ? 'drag-over' : ''} ${uploadFiles.length > 0 ? 'has-files' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  style={{ display: 'none' }}
                  disabled={isUploading}
                />
                <div className="dropzone-content">
                  <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p className="dropzone-text">
                    {isDragOver ? 'Drop photos here...' : 'Drag & drop photos here, or click to browse'}
                  </p>
                  <span className="dropzone-hint">Up to 20 photos • JPG, PNG, GIF, WEBP</span>
                </div>
              </div>

              {uploadFiles.length > 0 && (
                <div className="upload-preview-grid">
                  {uploadFiles.map((file, index) => (
                    <div key={index} className={`upload-preview-item ${file.status}`}>
                      <img src={file.preview} alt={file.file.name} />
                      <div className="preview-overlay">
                        {file.status === 'pending' && (
                          <button
                            className="remove-file-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                            disabled={isUploading}
                          >
                            ×
                          </button>
                        )}
                        {file.status === 'uploading' && (
                          <div className="upload-spinner" />
                        )}
                        {file.status === 'success' && (
                          <svg className="success-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        {file.status === 'error' && (
                          <span className="error-mark">!</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {uploadMessage && (
                <div className={`upload-message ${uploadMessage.type}`}>
                  {uploadMessage.text}
                </div>
              )}

              <button
                className="upload-submit-btn"
                onClick={handleUpload}
                disabled={isUploading || uploadFiles.length === 0}
              >
                {isUploading ? 'Uploading...' : `Upload ${uploadFiles.length > 0 ? uploadFiles.length : ''} Photo${uploadFiles.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default WeddingPhotosPage;
